import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "../context/TranslationContext";
import { isMockDocumentId } from "../helpers/documentCategories";

// Loaded on demand so pdf.js and the HEIC decoder stay out of the main bundle.
const loadPdf = () => import("../helpers/pdfRenderer");
const loadHeic = () => import("../helpers/heicConverter");

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
const CONVERT_TIMEOUT_MS = 45000;

const extensionOf = (doc) => ((doc?.name || "").split(".").pop() || "").toLowerCase();

// "pdf" | "image" | "heic" | null (null = the browser cannot show it inline, so it is downloaded as before).
export const viewerKind = (doc) => {
  const type = (doc?.fileType || "").toLowerCase();
  const ext = extensionOf(doc);
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (type.includes("heic") || type.includes("heif") || ext === "heic" || ext === "heif") return "heic";
  if (type.startsWith("image/") || IMAGE_EXTENSIONS.includes(ext)) return "image";
  return null;
};

// Word/Excel and similar files cannot be shown in the browser, so "Visualizar" downloads them.
const DOWNLOAD_ONLY_EXTENSIONS = ["doc", "docx", "xls", "xlsx", "csv", "ppt", "pptx", "odt", "ods", "odp", "rtf", "zip", "rar", "7z"];
const DOWNLOAD_ONLY_TYPE_PATTERN = /msword|ms-excel|ms-powerpoint|officedocument|opendocument|spreadsheet|presentation|wordprocessing|text\/csv|zip|rar|7z/;

// Docrio often stores files without an extension and as application/octet-stream, so anything that is
// not clearly an office/archive file is opened in the viewer, which detects the real type from its bytes.
export const opensInViewer = (doc) => {
  if (viewerKind(doc)) return true;
  const type = (doc?.fileType || "").toLowerCase();
  if (DOWNLOAD_ONLY_TYPE_PATTERN.test(type)) return false;
  return !DOWNLOAD_ONLY_EXTENSIONS.includes(extensionOf(doc));
};

const ascii = (bytes, start, end) => String.fromCharCode(...bytes.slice(start, end));

// Identifies PDFs and images from their first bytes: { kind, mime } or null.
export const sniffKind = (buffer) => {
  const bytes = new Uint8Array(buffer || new ArrayBuffer(0)).slice(0, 1024);
  if (bytes.length < 4) return null;
  const head = ascii(bytes, 0, bytes.length);
  if (head.includes("%PDF-")) return { kind: "pdf", mime: "application/pdf" };
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return { kind: "image", mime: "image/jpeg" };
  if (bytes[0] === 0x89 && ascii(bytes, 1, 4) === "PNG") return { kind: "image", mime: "image/png" };
  if (ascii(bytes, 0, 4) === "GIF8") return { kind: "image", mime: "image/gif" };
  if (ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 12) === "WEBP") return { kind: "image", mime: "image/webp" };
  if (ascii(bytes, 0, 2) === "BM") return { kind: "image", mime: "image/bmp" };
  if (ascii(bytes, 4, 8) === "ftyp") {
    const brand = ascii(bytes, 8, 12).toLowerCase();
    if (["heic", "heix", "hevc", "hevx", "heim", "heis", "mif1", "msf1"].includes(brand)) {
      return { kind: "heic", mime: "image/heic" };
    }
  }
  return null;
};

const mimeFor = (kind, doc) => {
  if (kind === "pdf") return "application/pdf";
  const type = (doc?.fileType || "").toLowerCase();
  if (type.startsWith("image/")) return type;
  if (kind === "heic") return "image/heic";
  const ext = extensionOf(doc);
  return `image/${ext === "jpg" ? "jpeg" : ext || "jpeg"}`;
};

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error("preview timed out")), ms))]);

// Reads the body while reporting progress (0-100) when the size is known.
async function readWithProgress(resp, onProgress) {
  const total = Number(resp.headers?.get?.("content-length")) || 0;
  if (!resp.body?.getReader || !total) return resp.arrayBuffer();
  const reader = resp.body.getReader();
  const chunks = [];
  let received = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress(Math.min(99, Math.round((received / total) * 100)));
  }
  const all = new Uint8Array(received);
  let offset = 0;
  chunks.forEach((chunk) => {
    all.set(chunk, offset);
    offset += chunk.length;
  });
  return all.buffer;
}

const stageText = ({ stage, progress, page, pages }, t) => {
  switch (stage) {
    case "download":
      return progress == null
        ? t("documents.viewer.downloading")
        : t("documents.viewer.downloadingProgress").replace("{progress}", String(progress));
    case "prepare":
      return t("documents.viewer.preparing");
    case "convert":
      return t("documents.viewer.converting");
    case "render":
      return pages
        ? t("documents.viewer.renderingPage").replace("{page}", String(page)).replace("{pages}", String(pages))
        : t("documents.viewer.rendering");
    default:
      return t("documents.viewer.opening");
  }
};

// Docrio serves every file as application/octet-stream, which makes browsers download it. We fetch the
// bytes ourselves (the signed-URL bucket allows CORS) and show them with the right type instead
// (TDDS-3494, ported from the BHP client portal). `url` may arrive after the viewer opens.
export default function DocumentViewer({ doc, url, onClose }) {
  const { t } = useTranslation();
  const kind = viewerKind(doc);
  const name = doc?.name || t("documents.viewer.untitled");
  const [state, setState] = useState({ status: "loading", objectUrl: "", displayUrl: "", note: "" });
  const [progress, setProgress] = useState({ stage: "open" });
  const pagesRef = useRef(null);
  const closeRef = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  // Fetch the renderer in parallel with the document instead of after it.
  useEffect(() => {
    if (kind === "pdf") loadPdf().then((m) => m.preloadPdf()).catch(() => {});
    if (kind === "heic") loadHeic().then((m) => m.preloadHeic()).catch(() => {});
  }, [kind]);

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    let objectUrl = "";
    let convertedUrl = "";
    (async () => {
      try {
        setProgress({ stage: "download" });
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`download failed: ${resp.status}`);
        const buffer = await readWithProgress(resp, (p) => !cancelled && setProgress({ stage: "download", progress: p }));
        if (cancelled) return;
        const sniffed = kind ? null : sniffKind(buffer);
        const resolvedKind = kind || sniffed?.kind || null;
        const blob = new Blob([buffer], {
          type: sniffed?.mime || (resolvedKind ? mimeFor(resolvedKind, doc) : "application/octet-stream"),
        });
        objectUrl = URL.createObjectURL(blob);
        if (!resolvedKind) {
          setState({ status: "unsupported", objectUrl, displayUrl: "", note: "" });
        } else if (resolvedKind === "pdf") {
          setProgress({ stage: "prepare" });
          const { renderPdf } = await loadPdf();
          if (cancelled) return;
          setState({ status: "rendering", kind: "pdf", objectUrl, displayUrl: "", note: "" });
          const { pages, rendered } = await renderPdf(new Uint8Array(buffer), pagesRef.current, {
            isCancelled: () => cancelled,
            onPage: (page, total) => !cancelled && setProgress({
              stage: "render",
              page,
              pages: total,
              progress: Math.round(((page - 1) / total) * 100),
            }),
            pageLabel: (page, total) => tRef.current("documents.viewer.pageLabel")
              .replace("{page}", String(page))
              .replace("{pages}", String(total)),
          });
          if (cancelled) return;
          setState({
            status: "ready",
            kind: "pdf",
            objectUrl,
            displayUrl: "",
            note: rendered < pages
              ? tRef.current("documents.viewer.truncated")
                .replace("{rendered}", String(rendered))
                .replace("{pages}", String(pages))
              : "",
          });
        } else if (resolvedKind === "heic") {
          // "Baixar" keeps the original HEIC; only the preview is converted when the browser can't show it.
          setProgress({ stage: "prepare" });
          const heic = await loadHeic();
          let displayUrl = objectUrl;
          if (!(await heic.canDisplayNatively(objectUrl))) {
            if (cancelled) return;
            setProgress({ stage: "convert" });
            const jpeg = await withTimeout(heic.heicToJpeg(blob), CONVERT_TIMEOUT_MS);
            if (cancelled) return;
            convertedUrl = URL.createObjectURL(jpeg);
            displayUrl = convertedUrl;
          }
          if (cancelled) return;
          setState({ status: "ready", kind: "heic", objectUrl, displayUrl, note: "" });
        } else {
          setState({ status: "ready", kind: "image", objectUrl, displayUrl: objectUrl, note: "" });
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to preview document:", error);
        setState({ status: "error", objectUrl, displayUrl: "", note: "" });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (convertedUrl) URL.revokeObjectURL(convertedUrl);
    };
  }, [url, kind, doc]);

  // Falls back to the signed URL itself, so "Baixar" still works if the preview fetch fails.
  const downloadHref = state.objectUrl || url;
  const working = state.status === "loading" || state.status === "rendering";
  const shownKind = state.kind || kind;

  return (
    <div className="docs-viewer-overlay" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div
        className="docs-viewer"
        role="dialog"
        aria-modal="true"
        aria-label={t("documents.viewer.dialogLabel").replace("{name}", name)}
        aria-busy={working}
      >
        <div className="docs-viewer-header">
          <div className="docs-viewer-title" title={name}>{name}</div>
          <div className="docs-viewer-actions">
            {downloadHref ? (
              <a className="docs-button" href={downloadHref} download={name} target="_blank" rel="noopener noreferrer">
                {t("documents.viewer.download")}
              </a>
            ) : null}
            <button
              type="button"
              className="docs-button docs-button-primary"
              ref={closeRef}
              onClick={onClose}
              aria-label={t("documents.viewer.closeLabel")}
            >
              {t("documents.viewer.close")}
            </button>
          </div>
        </div>
        {working ? (
          <div
            className={`docs-viewer-progress${progress.progress == null ? " is-indeterminate" : ""}`}
            role="progressbar"
            aria-label={stageText(progress, t)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress.progress ?? undefined}
          >
            <div
              className="docs-viewer-progress-bar"
              style={progress.progress == null ? undefined : { width: `${progress.progress}%` }}
            />
          </div>
        ) : null}
        <div className="docs-viewer-body">
          {working ? (
            <div className="docs-viewer-loading" role="status">
              <span className="docs-spinner" aria-hidden="true" />
              <span>{stageText(progress, t)}</span>
            </div>
          ) : null}
          {state.status === "error" ? (
            <div className="docs-viewer-status" role="alert">{t("documents.viewer.error")}</div>
          ) : null}
          {state.status === "unsupported" ? (
            <div className="docs-viewer-status" role="alert">{t("documents.viewer.unsupported")}</div>
          ) : null}
          {state.note ? <div className="docs-viewer-status">{state.note}</div> : null}
          {(shownKind === "image" || shownKind === "heic") && state.status === "ready" ? (
            <img className="docs-viewer-image" src={state.displayUrl} alt={name} />
          ) : null}
          {/* An unknown type may turn out to be a PDF, so its page container must already exist. */}
          {kind === "pdf" || kind === null ? <div className="docs-viewer-pages" ref={pagesRef} /> : null}
        </div>
      </div>
    </div>
  );
}

// "Visualizar" for a document: PDFs/images (and files of unknown type) open in the in-page viewer while
// the signed URL loads; office files, or callers without `fetchViewUrl`, go through `onDownload`.
// `onError(messageKey)` reports a failure to open the preview.
export function useDocumentPreview({ fetchViewUrl, onDownload, onError }) {
  const [viewer, setViewer] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const close = useCallback(() => setViewer(null), []);

  const open = async (doc) => {
    if (!fetchViewUrl || !opensInViewer(doc)) {
      if (onDownload) onDownload(doc);
      return;
    }
    if (isMockDocumentId(doc.id)) {
      if (onError) onError("documents.errors.notAvailable");
      return;
    }
    setViewer({ doc, url: null });
    setPreviewingId(doc.id);
    try {
      const url = await fetchViewUrl(doc);
      if (!url) throw new Error("missing signed url");
      setViewer((current) => (current && current.doc.id === doc.id ? { doc, url } : current));
    } catch (error) {
      console.error("Failed to open document:", error);
      setViewer((current) => (current && current.doc.id === doc.id ? null : current));
      if (onError) onError("documents.errors.download");
    } finally {
      setPreviewingId(null);
    }
  };

  const viewerElement = viewer
    ? <DocumentViewer doc={viewer.doc} url={viewer.url} onClose={close} />
    : null;

  return { open, previewingId, viewerElement };
}
